import { Check } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { scrollToTarget } from "@/lib/scroll";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "1%",
    unit: "per transaction",
    tagline: "Everything you need to start accepting USDC today.",
    features: ["No monthly fee", "Unlimited transactions", "Payment links & QR codes", "Non-custodial settlement"],
    cta: "Start Accepting USDC",
    soon: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "0.3%",
    unit: "per transaction",
    tagline: "For merchants scaling volume and needing hands-on support.",
    features: ["Priority support", "Custom integrations", "Everything in Free"],
    cta: "Coming Soon",
    soon: true,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="scroll-mt-24 border-t border-line py-24 sm:py-32"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-12">
        <Reveal className="text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Pay for what you process. Nothing else.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`pricing-card-${t.id}`}
                className={`card-hover relative flex h-full flex-col rounded-2xl border p-8 sm:p-10 ${
                  t.soon ? "border-line bg-card2" : "border-sgreen/30 bg-card2"
                }`}
              >
                {!t.soon && (
                  <span className="absolute -top-3 left-8 rounded-full bg-sgreen px-3.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-black">
                    Live now
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">{t.name}</h3>
                  {t.soon && (
                    <span
                      data-testid="pricing-pro-badge"
                      className="rounded-full border border-line bg-white/[0.04] px-3.5 py-1 font-mono text-[10px] uppercase tracking-widest text-mut"
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="mt-6">
                  <span className="font-mono text-5xl font-semibold tracking-tight text-white">
                    {t.price}
                  </span>
                  <span className="ml-2 text-sm text-mut">{t.unit}</span>
                </p>
                <p className="mt-4 text-sm leading-relaxed text-mut">{t.tagline}</p>
                <ul className="mt-8 flex-1 space-y-3.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-white/85">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sgreen/10">
                        <Check className="h-3 w-3 text-sgreen" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                {t.soon ? (
                  <button
                    disabled
                    data-testid="pricing-pro-button"
                    className="mt-10 w-full cursor-not-allowed rounded-full border border-line bg-white/[0.03] py-3.5 text-sm font-medium text-mut"
                  >
                    Coming Soon
                  </button>
                ) : (
                  <button
                    onClick={() => scrollToTarget("#demo")}
                    data-testid="pricing-free-button"
                    className="cta-glow mt-10 w-full rounded-full bg-sgreen py-3.5 text-sm font-semibold text-black"
                  >
                    {t.cta}
                  </button>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
